import withParams from "@src/components/widgets/HOC/withParams";
import React, { useMemo } from "react";
import { WidgetTemplateComponentType } from "@src/interface/Widget";
import { ILandingStandardWidgetHighlighProductParams } from "../interface";
import useCtaAction from "@src/components/widgets/hooks/useCtaAction";
import LandingHighlighProduct, {
  type ICompositionVariant,
} from "design-system/src/Components/WidgetPart/LandingHighlighProduct/LandingHighlighProduct";
import type { TAnimationVariant } from "design-system/src/Components/WidgetPart/LandingSectionBase/LandingSectionBase";
import useCompositionBox from "@src/components/widgets/hooks/useCompositionBox";
import htmlParse from "html-react-parser";
import { useTranslation } from "@src/hooks/useTranslation";
import { usePushAppsModal } from "@src/lib/WidgetPushApp/WidgetPushApp";
import {
  InnovationProductTileVisual,
  LandingStandardDualVideoBackground,
} from "design-system/src/Components/WidgetPart/LandingStandardMedia";
import { useLandingStandardDualMedia } from "../hooks/useLandingStandardDualMedia";
import { resolveDualResponsiveMedia } from "design-system/src/Components/WidgetPart/LandingStandardMedia/resolveResponsiveMedia";
import { renderLandingStandardSushiShopVideo } from "../renderLandingStandardSushiShopVideo";

function cmsHighlightMainVisualConfigured(raw: Record<string, unknown>): boolean {
  const keys = [
    "highlight_main_visual_desktop__source",
    "highlight_main_visual_mobile__source",
    "highlight_main_visual_desktop__image",
    "highlight_main_visual_mobile__image",
    "highlight_main_visual_desktop__video",
    "highlight_main_visual_mobile__video",
  ];
  return keys.some((k) => {
    const v = raw[k];
    return v !== undefined && v !== null && String(v).trim() !== "";
  });
}

const LandingStandardHighlightProduct: WidgetTemplateComponentType =
  withParams<ILandingStandardWidgetHighlighProductParams>((props) => {
    const { p, params } = props;
    const cta_action = p("cta_action");
    const isAddToCart: boolean = cta_action === "add_to_cart";
    const attachPushApps: boolean = p("attach_to_push_apps") === "1";
    const hideCta = p("hide_cta") === "1";
    const id_product = p("product") || p("id_product");
    const ctaLabel = p("cta_label") || "";
    const legacyMediaSource = (p("media_source") || "image").toLowerCase();
    const { t } = useTranslation("Product");

    const { generateClick, getSubmitLabel } = useCtaAction(id_product || "");
    const { prepareCompositions, prepareProduct } = useCompositionBox();
    const { openModal, canShowPushApps } = usePushAppsModal();

    const productModel = prepareProduct(p("id_product") || "");

    const rawParams = params as Record<string, unknown>;

    const cmsMainTouched = cmsHighlightMainVisualConfigured(rawParams);

    const useAnimation =
      (p("middle_use_animation_desktop") === "1" && !!p("animation_src")) ||
      (!cmsMainTouched &&
        legacyMediaSource === "animation" &&
        !!p("animation_src"));

    const useYoutubeLegacy =
      !cmsMainTouched &&
      !useAnimation &&
      legacyMediaSource === "video" &&
      !!(p("video_src") || "").trim();

    const mainDual = useMemo(() => {
      if (useYoutubeLegacy || useAnimation) {
        return { desktop: undefined, mobile: undefined };
      }
      return resolveDualResponsiveMedia(rawParams, "highlight_main_visual", {
        desktopImageKey: "product_image",
        mobileImageKey: "product_image",
      });
    }, [rawParams, useYoutubeLegacy, useAnimation]);

    const highlightBackgroundMedia = useLandingStandardDualMedia({
      rawParams,
      prefix: "highlight_background",
      legacy: {
        desktopImageKey: "background_media_desktop",
        mobileImageKey: "background_media_mobile",
      },
    });

    const onClick = generateClick(
      cta_action && cta_action === "open_link"
        ? {
            type: cta_action,
            link: p("link") || "#",
          }
        : {
            type: cta_action || "product_card",
          }
    );

    const compositions: ICompositionVariant | undefined = useMemo(() => {
      const source = p("composition_source");
      if (source === "box") {
        const productIds: string[] = (p("box_products") || "")
          .split(",")
          .map((id) => id.trim());
        const productQuantity: number[] = (p("box_products_itemnbr") || "")
          .split(",")
          .map((i) => +i);
        const newProductIds: string[] = (p("box_products_new") || "")
          .split(",")
          .map((id) => id.trim());
        return {
          type: "box",
          list:
            prepareCompositions(productIds, productQuantity, newProductIds) ||
            [],
        } as ICompositionVariant;
      } else if (source === "paragraph") {
        return {
          type: "paragraph",
          text: htmlParse(p("composition_paragraph") || ""),
        } as ICompositionVariant;
      }
      return undefined;
    }, [p, prepareCompositions]);

    const displayFromMention =
      p("product_display_from_mention") === "1"
        ? t("À partir de", "Product")
        : undefined;

    const vaePrice = productModel?.getPriceVae();

    const vadPrice = productModel?.getPriceVad();

    const handleClick = () => {
      if (attachPushApps && canShowPushApps()) {
        openModal();
      } else {
        onClick();
      }
    };

    const isTopTitle = p("style_template") === "top-center-title";
    const heroAspectRatio = isTopTitle ? 3 / 2 : 1;

    const renderMiddleMedia = useMemo(() => {
      if (useAnimation || useYoutubeLegacy) {
        return undefined;
      }
      const fb =
        (mainDual.desktop?.kind === "image" && mainDual.desktop.imageId) ||
        (mainDual.mobile?.kind === "image" && mainDual.mobile.imageId) ||
        p("product_image") ||
        productModel?.getPictureByType("rollingstart")?.id_object_picture ||
        "-1";
      return () => (
        <InnovationProductTileVisual
          desktop={mainDual.desktop}
          mobile={mainDual.mobile}
          fallbackImageId={fb}
          alt=""
          aspectRatio={heroAspectRatio}
          desktopImageWidth={904}
          videoPlayback="ambient"
          renderVideo={renderLandingStandardSushiShopVideo}
        />
      );
    }, [
      useAnimation,
      useYoutubeLegacy,
      mainDual,
      heroAspectRatio,
      p,
      productModel,
    ]);

    return (
      <LandingHighlighProduct
        variant={p("style_template")}
        title={p("title") || ""}
        highlitedTitle={p("subtitle")}
        ctaLabel={isAddToCart ? getSubmitLabel(false, ctaLabel) : ctaLabel}
        description={p("description")}
        image={useYoutubeLegacy || useAnimation ? p("product_image") : undefined}
        video={useYoutubeLegacy ? p("video_src") : undefined}
        animation={
          useAnimation ? (p("animation_src") as TAnimationVariant | undefined) : undefined
        }
        renderMiddleMedia={renderMiddleMedia}
        onClick={handleClick}
        backgroundImage={highlightBackgroundMedia.backgroundImageIds}
        backgroundBehindContainer={
          <LandingStandardDualVideoBackground
            dual={highlightBackgroundMedia.dual}
            renderVideo={renderLandingStandardSushiShopVideo}
          />
        }
        imageLeft={p("image_position") === "image-left"}
        compositionLabel={p("composition") || ""}
        compositions={compositions}
        price={productModel?.formatPrice()}
        priceSubtitle={
          productModel && productModel.getQuantity() > 1
            ? productModel.displayPieces()
            : undefined
        }
        product_display_from_mention={displayFromMention}
        product_price_vae={vaePrice}
        product_price_vad={vadPrice}
        vaeLabel={t("À emporter", "Product")}
        vadLabel={t("En livraison", "Product")}
        hideCta={hideCta}
      />
    );
  });

export default LandingStandardHighlightProduct;
